
      // Embed mode. When the page is shown INSIDE the Grafana dashboard's Video panel
      // (game-in-dashboard, the mentor's Spice-Runner layout), we pass ?embed=game-only so
      // the page renders JUST the game — no "Live Observatory" section — avoiding a
      // Grafana-in-game-in-Grafana hall of mirrors and keeping the panel clean and focused.
      // The standalone site (no flag) keeps the full dashboard-below-the-game experience.
      const embedMode = new URLSearchParams(location.search).get('embed');
      const gameOnly = embedMode === 'game-only';
      if (gameOnly) {
        document.documentElement.classList.add('cy-embed-game-only');
        const obs = document.getElementById('cy-observatory');
        if (obs) obs.hidden = true;
      }

      // Scroll cue: clicking it smooth-scrolls to the dashboard; it fades out once the
      // visitor scrolls at all (purpose served). Inert in game-only embed mode.
      if (!gameOnly) {
        const cue = document.getElementById('cy-scrollcue');
        const cueBtn = document.getElementById('cy-scrollcue-btn');
        const reduceMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        cueBtn?.addEventListener('click', () => {
          document
            .getElementById('cy-observatory')
            ?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        });
        globalThis.addEventListener(
          'scroll',
          () => {
            if (window.scrollY > 40 && cue) cue.dataset.scrolled = 'true';
          },
          { passive: true },
        );
      }

      // Live Observatory below the game — the REAL Grafana dashboard, inline. Grafana
      // Cloud sends CSP `frame-ancestors 'none'`, so the dashboard can't be iframed from
      // this origin directly. Instead we iframe it through the Worker's same-origin reverse
      // proxy at `${API}/public-dashboards/<token>`, which strips the frame-blocking
      // headers. We need the API base (the Worker) and a PUBLIC-dashboard URL (so there's a
      // token to proxy). If either is missing, or the frame errors, we fall back to the
      // open-in-new-tab button. Hidden entirely in game-only embed mode (inside the Video
      // panel), where the page is already the game.
      const apiBase = (import.meta.env.VITE_API_BASE_URL || '').trim().replace(/\/$/, '');
      const grandUrl = (import.meta.env.VITE_GRAFANA_GRAND_URL || '').trim();
      const publicUrl = (import.meta.env.VITE_GRAFANA_DASHBOARD_URL || '').trim();

      // Build the proxied embed URL from the public-dashboard token, if we have both the
      // Worker API base and a /public-dashboards/<token> URL. The Worker proxies the
      // /public-dashboards path at its ROOT (not under /dash) so the Grafana SPA sees
      // window.location.pathname === "/public-dashboards/<token>", which its boot script
      // requires — otherwise it shows "Error loading Grafana".
      function buildProxyEmbedUrl() {
        if (!apiBase || !publicUrl) return '';
        try {
          const u = new URL(publicUrl);
          if (!u.pathname.includes('/public-dashboards/')) return '';
          // Serve kiosk (chromeless) + dark theme + periodic refresh inside the frame.
          const params = new URLSearchParams(u.search);
          params.set('kiosk', '1');
          params.set('theme', 'dark');
          if (!params.has('refresh')) params.set('refresh', '30s');
          return `${apiBase}${u.pathname}?${params.toString()}`;
        } catch {
          return '';
        }
      }

      if (!gameOnly) {
        const frame = document.getElementById('cy-dash-frame');
        const cta = document.getElementById('cy-dash-cta');
        const openBtn = document.getElementById('cy-dash-open');
        const proxyUrl = buildProxyEmbedUrl();
        // The fallback button always points at the real dashboard (grand if set, else public).
        const openUrl = grandUrl || publicUrl;
        if (cta && openUrl) cta.href = openUrl;
        // Always-on header button: opens the full-size dashboard in a new tab. Prefer the
        // PUBLIC dashboard URL so anyone (unauthenticated) can view it; fall back to the
        // authenticated "grand" URL only if no public URL is set. Revealed whenever we have
        // any dashboard URL at all — independent of whether the inline embed loads.
        if (openBtn) {
          const fullUrl = publicUrl || grandUrl;
          if (fullUrl) {
            openBtn.href = fullUrl;
            openBtn.hidden = false;
          }
        }

        if (frame && proxyUrl) {
          // Reveal the CTA fallback only if the frame fails to load within a grace window.
          let loaded = false;
          frame.addEventListener('load', () => {
            loaded = true;
            frame.hidden = false;
            if (cta) cta.hidden = true;
          });
          frame.addEventListener('error', () => {
            if (cta && openUrl) cta.hidden = false;
          });
          frame.src = proxyUrl;
          frame.hidden = false;
          // Safety net: if the embed hasn't loaded after 6s (proxy/browser refused it),
          // surface the open-in-new-tab button so the dashboard is always reachable.
          setTimeout(() => {
            if (!loaded && cta && openUrl) cta.hidden = false;
          }, 6000);
        } else if (cta && openUrl) {
          // No proxy available (no API base or no public token) — link out instead.
          cta.hidden = false;
        }
      }
    